'use client'

import { ArrowLeft24 } from '@vinted/monochrome-icons'
import { Button, Cell, Container, Dialog, Navigation, Spacer, Text } from '@vinted/web-ui'
import { ReactNode } from 'react'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'

import {
  AccessChannel,
  FaqEntryType,
} from '@marketplace-web/help-center/help-center-faq-entry-url-data'

type Props = {
  show: boolean
  onBack: () => void
}

const ClosetPromotionPreCheckoutHelpModal = ({ show, onBack }: Props) => {
  const translate = useTranslate('closet_promotion.pre_checkout_help_modal')
  const a11yTranslate = useTranslate('closet_promotion.a11y.actions')
  const closetPromotionRenameAbTest = useAbTest('closet_promotion_rename')
  const isClosetPromotionRenameAbTestOn =
    closetPromotionRenameAbTest?.variant === 'a' || closetPromotionRenameAbTest?.variant === 'b'

  function renderInfoBlock(type: string) {
    return (
      <Cell
        title={<Text as="h3" text={translate(`${type}.title`)} type={Text.Type.Title} />}
        body={<Text as="span" text={translate(`${type}.body`)} html />}
      />
    )
  }

  function renderBody() {
    return <Text as="p" text={translate('body')} type={Text.Type.Body} html />
  }

  function renderFaqNote() {
    const values = {
      'help-center': (chunks: Array<ReactNode>) => (
        <HelpCenterFaqEntryUrl
          type={FaqEntryType.ClosetPromotion}
          accessChannel={AccessChannel.ProductLink}
          render={url => (
            <a href={url} target="_blank" rel="noreferrer">
              {chunks}
            </a>
          )}
          key="read-more-link"
        />
      ),
    }

    return (
      <Cell>
        <Text as="span" html text={translate('read_more', values)} type={Text.Type.Subtitle} />
      </Cell>
    )
  }

  const renderLearnMoreLink = () => {
    return (
      <HelpCenterFaqEntryUrl
        type={FaqEntryType.ClosetPromotion}
        accessChannel={AccessChannel.ProductLink}
        render={url => (
          <a href={url} target="_blank" rel="noreferrer">
            <Text as="span" text={translate('learn_more')} type={Text.Type.Body} clickable />
          </a>
        )}
        key="read-more-link"
      />
    )
  }

  return (
    <Dialog show={show} hasScrollableContent>
      <div className="u-fill-width">
        <ScrollableArea>
          <Navigation
            left={
              <Button
                theme="amplified"
                iconName={ArrowLeft24}
                styling={Button.Styling.Flat}
                testId="closet-promotion-pre-checkout-help-back-button"
                onClick={onBack}
                aria={{ 'aria-label': a11yTranslate('back') }}
              />
            }
            body={
              <Text
                as="h2"
                text={isClosetPromotionRenameAbTestOn ? translate('title_v2') : translate('title')}
                type={Text.Type.Title}
              />
            }
          />
          {isClosetPromotionRenameAbTestOn ? (
            <Container>
              {renderBody()}
              <Spacer size={Spacer.Size.Large} />
              {renderLearnMoreLink()}
            </Container>
          ) : (
            <>
              {renderInfoBlock('item_selection')}
              {renderInfoBlock('featured')}
              {renderFaqNote()}
            </>
          )}
        </ScrollableArea>
      </div>
    </Dialog>
  )
}

export default ClosetPromotionPreCheckoutHelpModal
